import { useEffect, useState } from "react";
import { useDashboardUI } from "../../../context/GlobalContext";
import { toast } from "react-toastify";
import promptService from "../../../services/promptService";
import { MAX_PROMPT_LENGTH, MIN_PROMPT_LENGTH, MIN_PROMPT_DESCRIPTION_LENGTH, userRoles } from "../../../helpers/constant";
import { validateUserRole } from "../../../helpers/helpers";
import { extractUserFromToken } from "../../../helpers/auth";
import Select from "react-select";


export default function CreatePromptModal({
  openCreateModal,
  onClose,
  onCreated,
}) {
  const { setPromptList, facilityList } = useDashboardUI();
  const currentUser = extractUserFromToken();
  const userFacilityId = parseInt(currentUser?.facility_id);  

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [contentError, setContentError] = useState("");
  const [isPromptRegenerating, setIsPromptRegenerating] = useState(false);

  const raw = process.env.REACT_APP_PROMPT_GLOBAL_ALLOWED_FACILITY_IDS;
  const isGlobalAllowedForAll = raw === "*";

  const allowedFacilityIds = !raw || raw === "*"
    ? []
    : raw.split(",").map((id) => Number(id));

  const [shareAccess, setShareAccess] = useState("facility");
  const [selectedFacilities, setSelectedFacilities] = useState([userFacilityId]);

  const facilityOptions = facilityList.map((f) => ({
    value: f.facility_number,
    label: f.name,
  }));



  useEffect(() => {
    if (!submitted) return;

    const errors = validateForm();
    setNameError(errors.name ?? "");
    setDescriptionError(errors.description ?? "");
    setContentError(errors.content ?? "");
  }, [name, description, content, submitted]);

  if (!openCreateModal) return null;

  const validateForm = () => {
    const errors = {};
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedContent = content.trim();

    if (!trimmedName) {
      errors.name = "Please provide a name for the prompt.";
    }

    if (!trimmedDescription) {
      errors.description = "Please provide a description for the prompt.";
    } else if (trimmedDescription.length < MIN_PROMPT_DESCRIPTION_LENGTH) {
      errors.description = `Description must be at least ${MIN_PROMPT_DESCRIPTION_LENGTH} characters long.`;
    }

    if (!trimmedContent) {
      errors.content = "Please provide content for the prompt.";
    } else if (trimmedContent.length < MIN_PROMPT_LENGTH) {
      errors.content = `Prompt must be at least ${MIN_PROMPT_LENGTH} characters long.`;
    } else if (trimmedContent.length > MAX_PROMPT_LENGTH) {
      errors.content = `Prompt cannot exceed ${MAX_PROMPT_LENGTH} characters.`;
    }

    return errors;
  };

  const savePrompt = async () => {
    setSubmitted(true);

    const errors = validateForm();
    setNameError(errors.name ?? "");
    setDescriptionError(errors.description ?? "");
    setContentError(errors.content ?? "");

    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        content: content.trim(),
        is_published: isPublished,
        share_access: "facility"
      };

      if (canSeeGlobalOption) {
        payload.share_access = shareAccess;
        if (shareAccess === "facility") {
          if (selectedFacilities.length === 0) {
            toast.error("Please select at least one facility");
            setLoading(false);
            return;
          }
          payload.facility_numbers = selectedFacilities;
        }
      }

      const createdPrompt = await promptService.createPrompts(payload).then((res) => { 
        if (res?.data) {
          setPromptList((prev) => [res?.data, ...prev]);
          toast.success("Prompt created successfully");
        }   
      });

      // ✅ RESET STATE
      setName("");
      setDescription("");
      setContent("");
      setIsPublished(false);
      setSubmitted(false);

      // ✅ CLOSE MODAL
      onClose();

      // ✅ OPTIONAL CALLBACK
      onCreated?.(createdPrompt);

    } catch (error) {
      console.error(error);
      toast.error("Failed to create prompt");
    } finally {
      setLoading(false);
    }
  };


  const handleRegeneratePrompt = async () => {
    try {
      const errors = validateForm();
      if (errors?.content) {
        toast.error(errors.content);
        return;
      }
      setIsPromptRegenerating(true);

      const payload = { prompt: content };
      const res = await promptService.regeneratePrompt(payload);

      if (res && res.data && res.data.prompt) {
        let newPrompt = res.data.prompt.replace(/undefined$/i, "");

        // Start typing animation
        const typeOutText = (text, index = 0) => {
          if (index > text.length) {
            setIsPromptRegenerating(false);
            return;
          }
          setContent(text.slice(0, index));
          setTimeout(() => typeOutText(text, index + 1), 20);
        };

        typeOutText(newPrompt);
      } else {
        setIsPromptRegenerating(false);
      }
    } catch (err) {
      setIsPromptRegenerating(false);
      console.error("Error handleRegeneratePrompt:", err);
    }
  };

  const canSeeGlobalOption =
    validateUserRole(userRoles.SUPERADMIN) &&
    (isGlobalAllowedForAll ||
      allowedFacilityIds.includes(userFacilityId));


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center ">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        // onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-4xl rounded-2xl bg-white p-8 shadow-xl max-h-[100vh] overflow-y-auto">
        <h2 className="text-3xl font-semibold text-slate-900 mb-4">
          Create prompt 
        </h2>

        {/* Name */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for your prompt"
            className="w-full rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500"
          />
          {submitted && nameError && (
            <p className="mt-1 text-sm text-red-600">{nameError}</p>
          )}
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Description
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Enter your Description (minimum ${MIN_PROMPT_DESCRIPTION_LENGTH} characters required).`}
            className="w-full rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500"
          />
          {/* Error Message (now OUTSIDE the relative container) */}
          <div className="mt-1 flex items-center justify-between text-xs">
            {/* Left slot (error or empty space) */}
            <p className="min-h-[16px]">
              {submitted && descriptionError && (
                <span className="text-destructive">
                  {descriptionError}
                </span>
              )}
            </p>

            {/* Right slot (counter) */}
            <p className="text-gray-500">
              {description.length} character{description.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Content
          </label>

          {/* Textarea wrapper (KEY FIX) */}
          <div className="relative">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={canSeeGlobalOption ? 3 : 7}
              className="w-full resize-y rounded-xl border px-4 py-3 text-sm pr-12 focus:ring-2 focus:ring-indigo-500"
              placeholder={`Enter your prompt (minimum ${MIN_PROMPT_LENGTH} characters required).`}
              disabled={isPromptRegenerating}
            />

            {/* Copilot Icon */}
            <button
              type="button"
              title="Get Better Prompts Using Genserve Copilot"
              onClick={handleRegeneratePrompt}
              className="absolute bottom-3 right-3 text-black transition"
            >
              {/* svg unchanged */}
              <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M9.75 14a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75Zm4.5 0a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75Z"></path><path d="M12 2c2.214 0 4.248.657 5.747 1.756.136.099.268.204.397.312.584.235 1.077.546 1.474.952.85.869 1.132 2.037 1.132 3.368 0 .368-.014.733-.052 1.086l.633 1.478.043.022A4.75 4.75 0 0 1 24 15.222v1.028c0 .529-.309.987-.565 1.293-.28.336-.636.653-.966.918a13.84 13.84 0 0 1-1.299.911l-.024.015-.006.004-.039.025c-.223.135-.45.264-.68.386-.46.245-1.122.571-1.941.895C16.845 21.344 14.561 22 12 22c-2.561 0-4.845-.656-6.479-1.303a19.046 19.046 0 0 1-1.942-.894 14.081 14.081 0 0 1-.535-.3l-.144-.087-.04-.025-.006-.004-.024-.015a13.16 13.16 0 0 1-1.299-.911 6.913 6.913 0 0 1-.967-.918C.31 17.237 0 16.779 0 16.25v-1.028a4.75 4.75 0 0 1 2.626-4.248l.043-.022.633-1.478a10.195 10.195 0 0 1-.052-1.086c0-1.331.282-2.498 1.132-3.368.397-.406.89-.717 1.474-.952.129-.108.261-.213.397-.312C7.752 2.657 9.786 2 12 2Zm-8 9.654v6.669a17.59 17.59 0 0 0 2.073.98C7.595 19.906 9.686 20.5 12 20.5c2.314 0 4.405-.594 5.927-1.197a17.59 17.59 0 0 0 2.073-.98v-6.669l-.038-.09c-.046.061-.095.12-.145.177-.793.9-2.057 1.259-3.782 1.259-1.59 0-2.738-.544-3.508-1.492a4.323 4.323 0 0 1-.355-.508h-.344a4.323 4.323 0 0 1-.355.508C10.704 12.456 9.555 13 7.965 13c-1.725 0-2.989-.359-3.782-1.259a3.026 3.026 0 0 1-.145-.177Zm6.309-1.092c.445-.547.708-1.334.851-2.301.057-.357.087-.718.09-1.079v-.031c-.001-.762-.166-1.26-.43-1.568l-.008-.01c-.341-.391-1.046-.689-2.533-.529-1.505.163-2.347.537-2.824 1.024-.462.473-.705 1.18-.705 2.32 0 .605.044 1.087.135 1.472.092.384.231.672.423.89.365.413 1.084.75 2.657.75.91 0 1.527-.223 1.964-.564.14-.11.268-.235.38-.374Zm2.504-2.497c.136 1.057.403 1.913.878 2.497.442.545 1.134.938 2.344.938 1.573 0 2.292-.337 2.657-.751.384-.435.558-1.151.558-2.361 0-1.14-.243-1.847-.705-2.319-.477-.488-1.318-.862-2.824-1.025-1.487-.161-2.192.139-2.533.529-.268.308-.437.808-.438 1.578v.02c.002.299.023.598.063.894Z"></path></svg>
            </button>

            {/* Loading overlay */}
            {isPromptRegenerating && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-xl">
                <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full" />
              </div>
            )}
          </div>

          {/* Error Message (now OUTSIDE the relative container) */}
          <div className="mt-1 flex items-center justify-between text-xs">
            {/* Left slot (error or empty space) */}
            <p className="min-h-[16px]">
              {submitted && contentError && (
                <span className="text-destructive">
                  {contentError}
                </span>
              )}
            </p>

            {/* Right slot (counter) */}
            <p className="text-gray-500">
              {content.length} character{content.length > 1 ? "s" : ""}
            </p>
          </div>
          
        </div>

        {/* 🔐 Share Access */}
        {/* Case 1: Superadmin – GLOBAL + FACILITY */}
        {canSeeGlobalOption && (
          <div className="mb-6">

            <label className="block mb-3 text-sm font-medium text-slate-700">
              Share access
            </label>


            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="shareAccess"
                  value="global"
                  checked={shareAccess === "global"}
                  onChange={() => setShareAccess("global")}
                />
                <span className="text-sm text-slate-700">Global</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="shareAccess"
                  value="facility"
                  checked={shareAccess === "facility"}
                  onChange={() => setShareAccess("facility")}
                />
                <span className="text-sm text-slate-700">Facility</span>
              </label>
            </div>

          </div>
        )}

        {/* 🏥 Facility Multi-select */}
        {(canSeeGlobalOption && shareAccess === "facility") && (
          <div className="mb-4">
            <Select
              isMulti
              options={facilityOptions}
              value={facilityOptions.filter((o) =>
                selectedFacilities.includes(o.value)
              )}
              onChange={(vals) =>
                setSelectedFacilities(vals.map((v) => v.value))
              }
              placeholder="Select facilities"
              classNamePrefix="react-select"
            />
          </div>

        )}



        {/* Publish toggle */}
        <div className="flex items-center gap-3 mb-8">
          <button
            type="button"
            onClick={() => setIsPublished(!isPublished)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition
              ${isPublished ? "bg-indigo-600" : "bg-gray-200"}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition
                ${isPublished ? "translate-x-5" : "translate-x-1"}`}
            />
          </button>
          <span className="text-sm font-medium text-slate-700">
            Publish
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border px-8 py-3 text-sm font-medium text-slate-700"
          >
            Cancel
          </button>

          <button
            disabled={loading || isPromptRegenerating}
            onClick={savePrompt}
            className="flex-1 rounded-xl bg-blue-500 px-8 py-3 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60 transition-colors"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

      </div>
    </div>
  );
}
